const WIDGET_SRC = 'https://cc.marketconsultive.de/widget.js';
const WIDGET_HOST_ID = 'mp-support-widget';

interface MpWidgetSettings {
    lang?: 'en';
    enabled?: boolean;
}

declare global {
    interface Window {
        mpWidgetSettings?: MpWidgetSettings;
        __mpWidgetLoaded?: boolean;
    }
}

type WidgetLanguage = 'de' | 'en';

// The widget only ships German and English strings, so every locale except German (fr, ...)
// falls back to the English widget.
const widgetLanguage = (locale: string): WidgetLanguage => (locale === 'de' ? 'de' : 'en');

// window.mpWidgetSettings always holds the settings the mounted widget was booted with:
// public/index.html sets it before the first load, and we replace it on every reload.
const mountedLanguage = (): WidgetLanguage => (window.mpWidgetSettings?.lang === 'en' ? 'en' : 'de');

/**
 * Keeps the customer care widget in the language the app is displayed in.
 *
 * widget.js is a self-guarding IIFE: it returns early when window.__mpWidgetLoaded is set,
 * reads window.mpWidgetSettings once into a closure, and exposes no re-init hook. Changing
 * its language therefore means unmounting it, clearing the guard and running the script again.
 *
 * public/index.html loads the widget in the initial language before Vue boots. This only
 * handles switching afterwards, and is a no-op unless the widget language actually changes
 * (de <-> fr both map to the German widget).
 */
export function useSupportWidget() {
    const syncSupportWidgetLanguage = (locale: string) => {
        const language = widgetLanguage(locale);
        if (language === mountedLanguage()) return;

        document.getElementById(WIDGET_HOST_ID)?.remove();
        document.querySelectorAll(`script[src="${WIDGET_SRC}"]`).forEach(script => script.remove());

        // The previous instance may still be waiting on its config request and would mount on
        // top of the new one. It holds the old settings object by reference and honours a false
        // "enabled", so disabling it there stops it from ever mounting.
        if (window.mpWidgetSettings) window.mpWidgetSettings.enabled = false;

        window.mpWidgetSettings = language === 'en' ? { lang: 'en' } : {};
        window.__mpWidgetLoaded = false;

        const script = document.createElement('script');
        script.src = WIDGET_SRC;
        script.async = true;
        document.head.appendChild(script);
    };

    return { syncSupportWidgetLanguage };
}
